import { roundHasClaimablePlayerPrize } from './tocPendingRound';
import type { TournamentOfChampions, TournamentOfChampionsRound } from './types';

export function isTocPlayerEliminated(toc: TournamentOfChampions | null | undefined): boolean {
  return toc?.isEliminated === true;
}

export function isTocPlayerStillInChampionship(toc: TournamentOfChampions | null | undefined): boolean {
  return !isTocPlayerEliminated(toc);
}

export function isRoundAdvancementQualified(round: TournamentOfChampionsRound | null | undefined): boolean {
  return round?.qualified === true;
}

function isFinalsRound(toc: TournamentOfChampions, round: TournamentOfChampionsRound): boolean {
  return round.roundIndex === Math.max(...toc.rounds.map((r) => r.roundIndex));
}

/** The round an analytics event is about: the pending end of round, the knockout round, else the current one. */
export function resolveTocAnalyticsRound(
  toc: TournamentOfChampions,
  pendingRound?: TournamentOfChampionsRound | null,
): TournamentOfChampionsRound | undefined {
  if (pendingRound) return pendingRound;
  const knockedOutIn = isTocPlayerEliminated(toc)
    ? toc.rounds.find((r) => r.isFinalized && r.qualified === false)
    : undefined;
  return (
    knockedOutIn ?? toc.rounds.find((r) => r.roundIndex === toc.currentRoundIndex) ?? toc.rounds[toc.currentRoundIndex]
  );
}

export function resolveTocAnalyticsIsQualified(
  toc: TournamentOfChampions,
  options?: { pendingRound?: TournamentOfChampionsRound | null },
): boolean {
  const round = resolveTocAnalyticsRound(toc, options?.pendingRound);
  // The Finals advance nobody: a finalist counts as qualified only when the Finals paid a prize.
  if (round?.isFinalized && isFinalsRound(toc, round)) return roundHasClaimablePlayerPrize(round);
  if (options?.pendingRound) {
    return isRoundAdvancementQualified(options.pendingRound);
  }
  return isTocPlayerStillInChampionship(toc);
}

export function hasAdvancementCutoff(
  playersToAdvanceLimit: number | null | undefined,
): playersToAdvanceLimit is number {
  return (
    typeof playersToAdvanceLimit === 'number' && Number.isFinite(playersToAdvanceLimit) && playersToAdvanceLimit > 0
  );
}

export function isLeaderboardRowInAdvancementZone(
  rankIndex: number,
  playersToAdvanceLimit: number | null | undefined,
): boolean {
  if (!hasAdvancementCutoff(playersToAdvanceLimit)) return false;
  return rankIndex < playersToAdvanceLimit;
}

export function shouldShowEliminationSummary(toc: TournamentOfChampions): boolean {
  if (!isTocPlayerEliminated(toc)) return false;

  const advancedFromRound0 = toc.rounds.some((r) => r.roundIndex === 0 && r.qualified === true);
  const participatedPastRound0 = toc.rounds.some(
    (r) => r.roundIndex > 0 && (r.playerTournamentKey != null || (r.isFinalized && r.playerPosition != null)),
  );

  return advancedFromRound0 || participatedPastRound0;
}

export function isEliminatedInFirstRoundOnly(toc: TournamentOfChampions | null | undefined): boolean {
  return !!toc && isTocPlayerEliminated(toc) && !shouldShowEliminationSummary(toc);
}
